// DeficiencyRulesTab — admin CRUD for configurable deficiency templates (UC-6.5).
//
// Same pattern as PiiRulesTab: list templates, add new, edit
// description/severity/priority/enabled, delete, reorder via priority.
// The canonical YAML is the audited baseline; the startup seeder upserts
// missing rows so operator edits survive a reload.

import { useState, useEffect, useCallback } from 'react';
import { Edit2, Trash2, Save, X, Loader2, AlertTriangle } from 'lucide-react';
import {
  getDeficiencyRules,
  createDeficiencyRule,
  updateDeficiencyRule,
  deleteDeficiencyRule,
  type DeficiencyRule,
  type DeficiencyRuleInput,
} from '../../api/admin';
import { useToastStore } from '../../stores/toast';

const EMPTY_FORM: DeficiencyRuleInput = {
  code: '',
  description: '',
  severity: 'medium',
  enabled: true,
  priority: 0,
};

const SEVERITY_CLASSES: Record<string, string> = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-100 text-slate-600',
};

export default function DeficiencyRulesTab() {
  const addToast = useToastStore((s) => s.addToast);
  const [rules, setRules] = useState<DeficiencyRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<DeficiencyRuleInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getDeficiencyRules();
      setRules(rows);
    } catch {
      addToast('Failed to load deficiency rules', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { void load(); }, [load]);

  const startEdit = (r: DeficiencyRule) => {
    setEditingId(r.id);
    setForm({
      code: r.code,
      description: r.description,
      severity: r.severity,
      enabled: r.enabled,
      priority: r.priority,
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.description.trim()) {
      addToast('Code and description are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload: DeficiencyRuleInput = {
        ...form,
        code: form.code.toUpperCase().trim(),
        description: form.description.trim(),
      };
      if (editingId) {
        await updateDeficiencyRule(editingId, payload);
        addToast('Deficiency rule updated', 'success');
      } else {
        await createDeficiencyRule(payload);
        addToast('Deficiency rule created', 'success');
      }
      resetForm();
      await load();
    } catch (e: any) {
      addToast(e?.response?.data?.detail || 'Failed to save deficiency rule', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setSaving(true);
    try {
      await deleteDeficiencyRule(id);
      addToast('Deficiency rule deleted', 'success');
      setConfirmDelete(null);
      await load();
    } catch (e: any) {
      addToast(e?.response?.data?.detail || 'Failed to delete rule', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Reorder: bump a rule's priority down (higher number) or up (lower).
  const handleReorder = async (rule: DeficiencyRule, delta: number) => {
    const newPri = Math.max(0, rule.priority + delta);
    setSaving(true);
    try {
      await updateDeficiencyRule(rule.id, { priority: newPri });
      await load();
    } catch (e: any) {
      addToast(e?.response?.data?.detail || 'Failed to reorder', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="animate-spin text-primary-500" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Configurable deficiency templates — broker-forwardable plain English keyed by finding code.
        The canonical YAML is the audited baseline; edits here persist across reloads.
      </p>

      {/* Editor form */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-800">
            {editingId ? 'Edit template' : 'Add template'}
          </h3>
          {editingId && (
            <button onClick={resetForm} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1">
              <X size={12} /> Cancel edit
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
          <div className="sm:col-span-3">
            <label className="block text-xs font-medium text-slate-500 mb-1">Code</label>
            <input
              className="input text-sm"
              placeholder="MISSING_CUSIP"
              value={form.code}
              disabled={!!editingId}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
          </div>
          <div className="sm:col-span-6">
            <label className="block text-xs font-medium text-slate-500 mb-1">Description</label>
            <input
              className="input text-sm"
              placeholder="Broker-forwardable plain English"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-slate-500 mb-1">Severity</label>
            <select
              className="input text-sm"
              value={form.severity}
              onChange={(e) => setForm({ ...form, severity: e.target.value as DeficiencyRuleInput['severity'] })}
            >
              <option value="high">high</option>
              <option value="medium">medium</option>
              <option value="low">low</option>
            </select>
          </div>
          <div className="sm:col-span-1">
            <label className="block text-xs font-medium text-slate-500 mb-1">Priority</label>
            <input
              type="number"
              className="input text-sm"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
            />
          </div>
        </div>
        <div className="flex items-center gap-3 pt-3">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-slate-300 text-primary-600 focus:ring-primary-400"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            Enabled
          </label>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary text-sm px-4 py-2 flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {editingId ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left">
                <th className="px-4 py-3 font-semibold text-slate-600 w-24">Order</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Code</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Description</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Severity</th>
                <th className="px-4 py-3 font-semibold text-slate-600">Enabled</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rules.map((r, i) => (
                <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleReorder(r, -1)}
                        disabled={saving || i === 0}
                        className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        title="Move up (lower priority number)"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => handleReorder(r, 1)}
                        disabled={saving || i === rules.length - 1}
                        className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                        title="Move down (higher priority number)"
                      >
                        ▼
                      </button>
                      <span className="text-xs text-slate-400 ml-1">{r.priority}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.code}</td>
                  <td className="px-4 py-3 text-slate-700">{r.description}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded ${SEVERITY_CLASSES[r.severity] ?? 'bg-slate-100 text-slate-600'}`}>
                      {r.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs ${r.enabled ? 'text-green-600' : 'text-slate-400'}`}>
                      {r.enabled ? '● active' : '○ disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => startEdit(r)}
                      className="text-slate-400 hover:text-primary-600 transition-colors mr-2"
                      aria-label={`Edit ${r.code}`}
                    >
                      <Edit2 size={15} />
                    </button>
                    {confirmDelete === r.id ? (
                      <span className="inline-flex items-center gap-1">
                        <button
                          onClick={() => handleDelete(r.id)}
                          disabled={saving}
                          className="text-xs text-red-600 hover:underline disabled:opacity-50"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmDelete(null)}
                          className="text-xs text-slate-500 hover:underline"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirmDelete(r.id)}
                        className="text-slate-400 hover:text-red-600 transition-colors"
                        aria-label={`Delete ${r.code}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rules.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    <AlertTriangle size={18} className="inline mr-2" />
                    No deficiency templates configured. Add one above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}